import clsx from 'clsx';
import { forwardRef } from 'react';

import { emphasisCSS } from './Emphasis.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';

const COMPONENT_NAME = 'Emphasis';

/**
 * The props for the Emphasis component.
 * @public
 */
export interface EmphasisProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {}

/**
 * The `Emphasis` component adds visual and semantic emphasis to stressed or essential content.
 * @public
 */
export const Emphasis = /* @__PURE__ */ forwardRef<HTMLElement, EmphasisProps>(
  (props, ref) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    return (
      <em
        ref={ref}
        className={clsx(emphasisCSS, consumerClassName)}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        {children}
      </em>
    );
  },
);

(Emphasis as typeof Emphasis & { displayName: string }).displayName =
  COMPONENT_NAME;
